import React from 'react';

import { dashboardCustomsInstructionApis } from '@apis/next/customsInstructionApis';
import { Alert } from '@components/Alert';
import { QueryContainer } from '@components/QueryContainer';
import { isInstructionOverdue } from '@components/utilities/Bookings/helpers';
import { calendarStrings } from '@data/calendar';
import { getInstructionStatusLabel, instructionStrings } from '@data/instruction';
import { useDeliveryInstruction } from '@hooks/queries/dashboard/useDeliveryInstruction';
import { getLastVisibleStatus } from '@models/Instruction';
import { CustomsInstructionHelper } from '@models/next/CustomsInstructionHelper';
import { UserHelper } from '@models/User';
import { ClassMap, makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { url } from '@utils/pages';
import { mapOptional } from '@utils/types';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { useLocation } from 'react-router-dom';

const strings = createStrings({
  ja: {
    writer: '記入者',
    deadline: '締め切り',
    status: 'ステータス',
    overdue: '期間切れ',
    detail: '詳細',
    cargoChangedAlert:
      'Prealertの更新に伴い、納品指示のトラックオプションが変更になりました。納品指示のトラックオプションを再確認してください。',
    useRcepChangedAlert:
      '【RCEPのご利用】の更新に伴い、通関書類の書類が変更になりました。通関書類を再確認してください。',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  innerContainer: {
    margin: '2rem 0 3.5rem 1rem',
    display: 'grid',
    gridTemplateColumns: 'minmax(auto,10%) minmax(auto,30%) minmax(auto,30%)',
    gridTemplateRows: 'minmax(41px,1fr) minmax(auto,1fr) minmax(auto,1fr) minmax(auto,1fr)',
    alignItems: 'center',
    ...common.textAlignCenter,
  },
  detailLink: {
    color: theme.palette.primary.main,
    cursor: 'pointer',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  badge: {
    height: 14,
    width: 14,
    borderRadius: '50%',
    backgroundColor: theme.palette.warning.dark,
    position: 'absolute',
    top: -5,
    right: -5,
    paddingBottom: 2,
    zIndex: 2,
  },
  disabled: {},
}));

export interface InstructionFormEntrancesProps {
  d2dNo: string;
  classes?: ClassMap<'root'>;
}

export const InstructionFormEntrances: React.FC<InstructionFormEntrancesProps> = (props) => {
  const { d2dNo } = props;
  const classes = { ...useStyles(), ...useCommonStyles() };
  const location = useLocation();
  const fromUrl = encodeURIComponent(`${location.pathname}${location.search}`);

  const customsInstructionQuery = dashboardCustomsInstructionApis.useGet(
    '/dashboard/customs-instruction/:d2d_no',
    {
      params: {
        d2d_no: d2dNo,
      },
    },
  );
  const deliveryInstruction = useDeliveryInstruction(d2dNo);

  const customsCurrentStatus = mapOptional(customsInstructionQuery.data, (d) =>
    CustomsInstructionHelper.getCurrentVisibleStatus(d),
  );
  const deliveryCurrentStatus = getLastVisibleStatus(deliveryInstruction.data?.status ?? []);

  const isCustomsInstructionOverdue =
    customsCurrentStatus?.name === '未送信' &&
    isInstructionOverdue(customsInstructionQuery.data?.cutoff_date!);
  const isDeliveryInstructionOverdue =
    deliveryCurrentStatus?.name === '未送信' &&
    isInstructionOverdue(deliveryInstruction.data?.cutoff_date!);

  return (
    <QueryContainer queries={[customsInstructionQuery, deliveryInstruction]}>
      {customsInstructionQuery.data?.use_rcep_changed && (
        <Alert severity="error" open marginTop hideIcon>
          {`${strings.useRcepChangedAlert}`}
        </Alert>
      )}
      {deliveryInstruction.data?.cargo_changed && (
        <Alert severity="error" open marginTop hideIcon>
          {`${strings.cargoChangedAlert}`}
        </Alert>
      )}

      {deliveryInstruction.data?.simulator_error && (
        <Alert severity="error" open marginTop>
          {`【${instructionStrings.deliveryInstruction}】${deliveryInstruction.data?.simulator_error}`}
        </Alert>
      )}
      <div className={classes.innerContainer}>
        <div style={{ display: 'contents' }}>
          <div role="head">{}</div>
          <div role="head" className={classes.fontWeight700}>
            <span style={{ position: 'relative' }}>
              {customsInstructionQuery.data?.use_rcep_changed && <div className={classes.badge} />}
              {instructionStrings.customsInstruction}
            </span>
            {'　'}
            <a
              target="_blank"
              href={`${url('/admin/booking/CustomsInstruction')}?d2dNo=${d2dNo}&from=${fromUrl}`}
              className={classes.detailLink}
            >
              {strings.detail}
            </a>
          </div>
          <div role="head" className={classes.fontWeight700}>
            <span style={{ position: 'relative' }}>
              {deliveryInstruction.data?.cargo_changed && <div className={classes.badge} />}
              {instructionStrings.deliveryInstruction}
            </span>
            {'　'}
            <a
              target="_blank"
              href={`${url('/admin/booking/DeliveryInstruction')}?d2dNo=${d2dNo}&from=${fromUrl}`}
              className={classes.detailLink}
            >
              {strings.detail}
            </a>
          </div>
        </div>
        <div style={{ display: 'contents' }}>
          <div>{strings.writer}</div>
          <div>
            {customsInstructionQuery.data?.writer_company?.company_name || '-'}
            {'('}
            {customsInstructionQuery.data?.writer
              ? new UserHelper(customsInstructionQuery.data?.writer).fullname
              : '-'}
            {')'}
          </div>
          <div>
            {deliveryInstruction.data?.writer_company?.company_name || '-'}
            {'('}
            {deliveryInstruction.data?.writer
              ? new UserHelper(deliveryInstruction.data?.writer).fullname
              : '-'}
            {')'}
          </div>
        </div>
        <div style={{ display: 'contents' }}>
          <div>{strings.deadline}</div>
          <div>
            {customsInstructionQuery.data?.cutoff_date
              ? `${calendarStrings.yearMonthDay(
                  dayjs(customsInstructionQuery.data?.cutoff_date).toDate(),
                )}  16:00 ${formStrings.until}`
              : '-'}
          </div>

          <div>
            {deliveryInstruction.data?.cutoff_date
              ? `${calendarStrings.yearMonthDay(
                  dayjs(deliveryInstruction.data?.cutoff_date).toDate(),
                )}  16:00 ${formStrings.until}`
              : '-'}
          </div>
        </div>
        <div style={{ display: 'contents' }}>
          <div>{strings.status}</div>
          <div className={clsx(isCustomsInstructionOverdue && classes.textError)}>
            {customsCurrentStatus?.name
              ? getInstructionStatusLabel(customsCurrentStatus.name, true)
              : '-'}
            {isCustomsInstructionOverdue && `（${strings.overdue}）`}
          </div>
          <div className={clsx(isDeliveryInstructionOverdue && classes.textError)}>
            {deliveryCurrentStatus?.name
              ? getInstructionStatusLabel(deliveryCurrentStatus.name, true)
              : '-'}
            {isDeliveryInstructionOverdue && `（${strings.overdue}）`}
          </div>
        </div>
      </div>
    </QueryContainer>
  );
};
